import { createRootRouteWithContext, Outlet, redirect, useLocation } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import LayoutHeaderArea from '@/components/layout/LayoutHeaderArea'
import { GlobalStateModel } from '@/models.ts'
import { coreDataQueryOptions, enabledFeaturesQueryOptions, loginDataQueryOptions } from '@/queries.ts'
import { extractTypeLabels, recordTypeLabels } from '@/constants/lookups.ts'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import Loader from '@/components/shared/Loader'

async function getMsalToken(globalState: GlobalStateModel): Promise<string> {
    await Promise.resolve();   //< This call makes sure we're now running in a different MicroTask.  See https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/5796
    const accounts = globalState.pca.getAllAccounts()
    const apiRequest = {
        scopes: [`api://${globalState.azureAD.ClientId}/CAPSDAC.Web.All`]
    }
    return globalState.pca
        .acquireTokenSilent({
            ...apiRequest,
            account: accounts[0] || {},
        }).then(resp => resp.accessToken)
}

export const Route = createRootRouteWithContext<GlobalStateModel>()({
    component: Root,
    pendingComponent: () => (
        <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ position: 'fixed', inset: 0, backgroundColor: 'var(--background)', zIndex: 2000 }}
        >
            <Loader containerClass="py-4 spinner-navy" />
        </div>
    ),
    // notFoundComponent: () => <NotFound />,
    beforeLoad: async ({ context, location, params }) => {
        // Redirect if user has not accepted terms and conditions
        if (location.pathname !== "/terms-conditions" && localStorage.getItem("termsConditionsAccepted") !== "true") {
            // Save current path as query param to redirect back after accepting terms
            const params = new URLSearchParams();
            params.set("redirect", location.pathname);

            // Redirect to terms and conditions page
            throw redirect({
                to: `/terms-conditions?${params.toString()}`,
                throw: true
            })
        }
        context.msalTokenFn = () => { return getMsalToken(context) }
        // Fetch all data in parallel
        [context.coreData, context.loginDetail, context.enabledFeatures] = await Promise.all([
            context.queryClient.ensureQueryData(coreDataQueryOptions(context)),
            context.queryClient.ensureQueryData(loginDataQueryOptions(context)),
            context.queryClient.ensureQueryData(enabledFeaturesQueryOptions(context))
        ])
        context.coreData.errorMessageMap = new Map(context.coreData.errorMessages.map(e => [e.ErrorCode, e]));
        const fileTypes = [...new Set(context.coreData.tooltips.map(x => x.FileType))];
        context.hasFeature = (feature) => context.enabledFeatures?.find(f => f === feature) !== undefined;
        context.coreData.tooltipMap = new Map(fileTypes.map(ft =>
            [ft, new Map(context.coreData.tooltips.filter(x => x.FileType === ft).map(x => [x.FieldName, x.Description]))]));
        // Update roles, extract type, and record type labels based on feature flags (Attendance, Education Programs)
        context.coreData.securityRoles = context.coreData.securityRoles.filter(x => context.hasFeature('Attendance') || (x.CAPSDACRoleCode != 'ATTNView' && x.CAPSDACRoleCode != 'ATTNEdit'));
        context.coreData.securityRoles = context.coreData.securityRoles.filter(x => context.hasFeature('Education_Programs') || (x.CAPSDACRoleCode != 'PRGMView' && x.CAPSDACRoleCode != 'PRGMEdit'));
        context.recordTypeLabels = {
            ...recordTypeLabels,
            ...{
                ...(context.hasFeature('Attendance') && { 'ATTN': 'Attendance (ATTN)' }),
                ...(context.hasFeature('Education_Programs') && { 'PRGM': 'Education Programs (PRGM)' }),
            }
        };
        context.extractTypeLabels = {
            ...extractTypeLabels,
            ...{
                ...(context.hasFeature('Attendance') && { 'ATTN': 'Attendance (ATTN)' }),
                ...(context.hasFeature('Education_Programs') && { 'PRGM': 'Education Programs (PRGM)' }),
            }
        };

        if (context.loginDetail.userOrgs !== undefined) {
            // If leaid is available from params, save currentUserOrg in context
            // We also attempt to set currentUserOrg in the leaid route's beforeLoad() call, but because of
            // https://dev.azure.com/cde-ca-gov/CAPSDAC/_workitems/edit/2877 it seems safer to load it here when possible
            context.currentUserOrg = context.loginDetail.userOrgs.find(x => x.esiIdPath === (params as any)?.leaid)
        }
        return context
    },
    // To-Do: Replace useLoaderData with useRouteContext, then remove this loader method
    loader: ({ context }) => {
        return context
    },
})

function Root() {
    // Check if current route is an LEA route, and update currentLea from context if so.
    // // To-Do: Would this be better moved down into the <Layout> component?
    // const [currentLea, setCurrentLea] = useState<{
    //     id: string;
    //     name: string;
    // } | undefined>();

    // const leaMatch = useMatch({ from: '/$leaid', shouldThrow: false });
    // useEffect(() => {
    //     setCurrentLea( leaMatch ? leaMatch.context.currentLea : undefined );
    // }, [leaMatch]);
    // const currentLea = useMatch({ from: '/$leaid', shouldThrow: false })?.context?.currentLea;

    // Future To-Do: 
    // Tanstack Router currently doesn't allow nesting dynamic route segments inside pathless layout routes,
    // so we can't have separate pathless layout routes for /terms-conditions and the $leaid routes.
    // When this is supported, we should move LayoutHeaderArea to a pathless layout route so we don't need to
    // render it conditionally here.
    // Source: https://tanstack.com/router/v1/docs/framework/react/routing/routing-concepts#pathless-layout-routes

    const location = useLocation();

    // Client-side navigation doesn't reload the page, so the browser never resets
    // focus to the top of the document. Without this, keyboard/screen reader users
    // land wherever they clicked from on every new page and can't reach the skip
    // link, since it's now behind their current focus position.
    const isInitialRender = useRef(true);
    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }
        document.getElementById('content')?.focus();
    }, [location.pathname]);

    return (
        <>  
            { location.pathname == "/terms-conditions" ? 
                // Set custom classes to make div scrollable because scroll is disabled for html, body, #app (for other pages' header/sidebar layout)
                <div className="overflow-auto w-100 h-100">
                    <Outlet />
                    <TanStackRouterDevtools />
                </div>
            :
                <LayoutHeaderArea>
                    <Outlet />
                    <TanStackRouterDevtools />
                </LayoutHeaderArea>
            }
        </>
    )
}